/**
 * Centralized logging utility
 * 
 * Logging can be enabled/disabled via REACT_APP_ENABLE_LOGGING environment variable.
 * Set REACT_APP_ENABLE_LOGGING=true in your .env file to enable extensive logging.
 * 
 * Usage:
 *   import logger from './utils/logger';
 *   logger.debug('ComponentName', 'Debug message', { data });
 *   logger.info('ComponentName', 'Info message', { data });
 *   logger.warn('ComponentName', 'Warning message', { data });
 *   logger.error('ComponentName', 'Error message', error, { context });
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: any;
}

class Logger {
  private enabled: boolean;
  private logLevel: LogLevel;

  constructor() {
    // Check if logging is enabled via environment variable
    this.enabled = process.env.REACT_APP_ENABLE_LOGGING === 'true';
    // Default log level - can be extended to support different levels
    this.logLevel = 'debug';
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.enabled) return false;
    
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const currentLevelIndex = levels.indexOf(this.logLevel);
    const messageLevelIndex = levels.indexOf(level);
    
    return messageLevelIndex >= currentLevelIndex;
  }

  private formatMessage(
    component: string,
    message: string,
    context?: LogContext,
    error?: Error | any
  ): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` | Context: ${JSON.stringify(context)}` : '';
    const errorStr = error ? ` | Error: ${error instanceof Error ? error.message : String(error)}` : '';
    return `[${timestamp}] [${component}] ${message}${contextStr}${errorStr}`;
  }

  private log(
    level: LogLevel,
    component: string,
    message: string,
    context?: LogContext,
    error?: Error | any
  ): void {
    if (!this.shouldLog(level)) return;

    const formattedMessage = this.formatMessage(component, message, context, error);
    const logData = {
      component,
      message,
      context,
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error,
      timestamp: new Date().toISOString()
    };

    switch (level) {
      case 'debug':
        console.debug(formattedMessage, logData);
        break;
      case 'info':
        console.info(formattedMessage, logData);
        break;
      case 'warn':
        console.warn(formattedMessage, logData);
        break;
      case 'error':
        console.error(formattedMessage, logData);
        break;
    }
  }

  debug(component: string, message: string, context?: LogContext): void {
    this.log('debug', component, message, context);
  }

  info(component: string, message: string, context?: LogContext): void {
    this.log('info', component, message, context);
  }

  warn(component: string, message: string, context?: LogContext): void {
    this.log('warn', component, message, context);
  }

  error(component: string, message: string, error?: Error | any, context?: LogContext): void {
    this.log('error', component, message, context, error);
  }

  // Helper method for API calls
  apiCall(component: string, method: string, url: string, data?: any): void {
    this.debug(component, `API ${method} ${url}`, { method, url, data });
  }

  apiResponse(component: string, method: string, url: string, status: number, data?: any): void {
    this.debug(component, `API ${method} ${url} - Response ${status}`, { method, url, status, data });
  }

  apiError(component: string, method: string, url: string, error: any): void {
    this.error(component, `API ${method} ${url} - Error`, error, {
      method,
      url,
      status: error?.response?.status,
      responseData: error?.response?.data
    });
  }

  // Helper method for component lifecycle
  componentMount(component: string, props?: any): void {
    this.debug(component, 'Component mounted', { props });
  }

  componentUnmount(component: string): void {
    this.debug(component, 'Component unmounting');
  }

  componentUpdate(component: string, prevProps?: any, nextProps?: any): void {
    this.debug(component, 'Component updated', { prevProps, nextProps });
  }

  // Helper method for user actions
  userAction(component: string, action: string, data?: any): void {
    this.info(component, `User action: ${action}`, { action, data });
  }

  // Helper method for state changes
  stateChange(component: string, stateKey: string, prevValue: any, newValue: any): void {
    this.debug(component, `State changed: ${stateKey}`, {
      stateKey,
      prevValue,
      newValue
    });
  }
}

// Export singleton instance
const logger = new Logger();
export default logger;

